import { Flex } from '@adobe/react-spectrum';
import { Accordion, AccordionItem } from '@react/react-spectrum/Accordion';
import qs from 'query-string';
import React, { useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { commonApi, handleApiCall } from '@exchange-frontends/api';
import { WithSkeleton } from '@exchange-frontends/components';
import { useHandleApiCall } from '@exchange-frontends/custom-hooks';
import { azSort } from '@exchange-frontends/utils';

import CenteredText from '../../../../components/styled-components/CenteredText';
import { useStore } from '../../store';
import FilterGroup from './FilterGroup';
import ListingTypePicker from './ListingTypePicker';
import { SearchField } from './SearchField';

// Styled Components

/**
 * accordion with specific margins
 */
const QuietAccordion = styled(Accordion)`
  margin: 0px;
`;

/**
 * quiet accordion item
 */
const QuietAccordionItem = styled(AccordionItem)`
  &&&& {
    border: none;
    :first-of-type {
      border-top: none;
    }
  }
`;

/**
 * checkbox group container that sets spacing to adjust for count
 */
export const CheckboxGroupContainer = styled.div`
  && {
    label {
      padding-right: 0;

      & > input + span + span {
        display: flex;
        width: 100%;
        justify-content: space-between;
      }
    }
  }
`;

const ccFilterRules = {
  product: {
    key: 'product',
    name: 'Product',
    shouldShowCount: false,
    shouldShowEllipsis: false,
    shouldUseKeyAsInputValue: true,
  },
  price: {
    type: 'radio',
    shouldShowCount: false,
  },
  tag: {
    shouldShowCount: false,
    maxFilters: 15,
  },
};

const ecFilterRules = {
  product: {
    key: 'product',
    name: 'Product',
    type: 'radio',
    shouldShowCount: false,
    shouldShowEllipsis: false,
    shouldUseKeyAsInputValue: true,
  },
  appType: {
    maxFilters: 15,
    shouldUseKeyAsInputValue: true,
  },
  partnerLevel: {
    key: 'partnerLevel',
    name: 'Partner level',
    type: 'radio',
    shouldShowCount: false,
    shouldUseKeyAsInputValue: true,
    skipSort: true,
    filters: [
      { name: 'All partner levels', key: 'All' },
      { name: 'Platinum', key: 'Platinum' },
      { name: 'Gold', key: 'Gold' },
      { name: 'Silver', key: 'Silver' },
    ],
  },
  industry: {
    shouldUseKeyAsInputValue: true,
  },
  country: {
    shouldUseKeyAsInputValue: true,
  },
};

const sppFilterRules = {
  geographies: {
    key: 'geographies',
    name: 'Geographies',
    type: 'radio',
    shouldShowCount: false,
    shouldUseKeyAsInputValue: true,
    sortOrder: 1,
    filters: [
      { name: 'All geographies', key: 'All' },
      { name: 'APAC', key: 'APAC' },
      { name: 'EMEA', key: 'EMEA' },
      { name: 'GLOBAL', key: 'GLOBAL' },
      { name: 'NAM', key: 'NAM' },
    ],
  },
  product: {
    key: 'product',
    name: 'Product',
    type: 'radio',
    shouldShowCount: false,
    shouldShowEllipsis: false,
    shouldUseKeyAsInputValue: true,
    maxFilters: 15,
    sortOrder: 2,
  },
  industry: {
    shouldUseKeyAsInputValue: true,
    maxFilters: 10,
    sortOrder: 3,
  },
  solutionType: {
    key: 'solutionType',
    name: 'Solution type',
    type: 'radio',
    shouldShowCount: false,
    shouldUseKeyAsInputValue: true,
    sortOrder: 4,
    filters: [
      { name: 'All solution types', key: 'All' },
      { name: 'Accredited Partner Solution', key: 'ACCREDITED' },
      { name: 'Rapid Deployment Solution', key: 'RAPID_DEPLOYMENT' },
    ],
  },
  partnerLevel: {
    key: 'partnerLevel',
    name: 'Partner level',
    type: 'radio',
    shouldShowCount: false,
    shouldUseKeyAsInputValue: true,
    skipSort: true,
    sortOrder: 5,
    filters: [
      { name: 'All partner levels', key: 'All' },
      { name: 'Platinum', key: 'Platinum' },
      { name: 'Gold', key: 'Gold' },
      { name: 'Silver', key: 'Silver' },
      { name: 'Bronze', key: 'Bronze' },
    ],
  },
};

export const appTypeFilters = [
  {
    label: 'App Builder',
    value: 'ABD',
  },
  {
    label: 'Catalog listings',
    value: 'S2S',
  },
  {
    label: 'Server-to-server integrations',
    value: 'SPISV',
  },
];

const getCountriesHandler = handleApiCall(commonApi.getCountries);
const getIndustriesHandler = handleApiCall(commonApi.getIndustries);

const ErrorFallback = () => (
  <CenteredText>
    We&apos;ve encountered an error trying to display the filters. Please try again in a few minutes.
  </CenteredText>
);

/**
 * This is the sidebar of the Browse application. It contains the search field and filters.
 * @returns {React.Component}
 */
const SideBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const filters = useStore((state) => state.filters);
  const loading = useStore((state) => state.loading);
  const cloud = useStore((state) => state.cloud);
  const productsList = useStore((store) => store.productsList);
  const { response: filterCategoryNames, loading: filterCategoryNamesLoading } = useHandleApiCall({
    handler: useMemo(
      () =>
        handleApiCall(commonApi.staticData, {
          slice: 'categories',
          cloud,
        }),
      [cloud]
    ),
    shouldShowError: false,
    defaultLoadingState: true,
  });

  const sortByChangedByUser = sessionStorage.getItem('sortByChangedByUser');
  const parsedSearch = new URLSearchParams(location.search);
  const defaultListingTypes = {
    cc: 'plugins',
    ec: 'Applications',
  };
  const listingType = parsedSearch.get('listingType') ?? defaultListingTypes[cloud];
  // Define configurations for different cloud types
  const cloudTypeConfigs = {
    ec: {
      showSearchAndFilters: true,
      isEC: parsedSearch.get('product') !== 'SIGN',
      isSolutions: listingType === 'solutions',
    },
    cc: {
      showSearchAndFilters: listingType === 'plugins',
      isEC: false,
      isSolutions: false,
    },
  };

  // Get the current configuration based on the cloud type
  const currentConfig = cloudTypeConfigs[cloud] || {};
  const shouldShowSearchAndFilters = currentConfig.showSearchAndFilters;
  const isEC = currentConfig.isEC;
  const isSolutions = currentConfig.isSolutions;

  // Determine sidebar filter rules based on cloud and listing type
  const filterRules = useMemo(() => {
    const filterRuleMap = {
      cc: {
        default: ccFilterRules,
      },
      ec: {
        Applications: ecFilterRules,
        solutions: sppFilterRules,
        default: ecFilterRules,
      },
    };

    const cloudRules = filterRuleMap[cloud] || {};
    return cloudRules[listingType] || cloudRules.default || {};
  }, [cloud, listingType]);

  const { response: countries, loading: countriesLoading } = useHandleApiCall({
    shouldFetch: Boolean(cloud && cloud !== 'cc'),
    handler: getCountriesHandler,
    shouldShowError: false,
    defaultLoadingState: true,
  });

  const { response: industries, loading: industriesLoading } = useHandleApiCall({
    shouldFetch: Boolean(cloud && cloud !== 'cc'),
    handler: getIndustriesHandler,
    shouldShowError: false,
    defaultLoadingState: true,
  });

  const getUrlFilterValue = (filterKey, inputType) => {
    const parsedSearch = qs.parse(location.search);
    return inputType === 'radio' ? parsedSearch[filterKey] : [].concat(parsedSearch[filterKey]);
  };

  const sortedFilterCategories = useMemo(() => {
    const categories = Object.keys(filters ?? {}).map((key) => {
      let categoryFilters = filters[key];
      const staticList = { country: countries, industry: industries, appType: appTypeFilters };
      if (staticList[key]) {
        categoryFilters = categoryFilters
          ?.sort((a) => (staticList[key].find((item) => item.value === a.name) ? -1 : 1))
          ?.map((filter) => {
            const item = staticList[key].find((item) => item?.value === filter.name);
            return {
              ...filter,
              key: filter.name,
              name: item?.label ?? filter.name,
            };
          });
        categoryFilters = [...new Set(categoryFilters?.map((filter) => filter?.name))].map((name) =>
          categoryFilters?.find((filter) => filter?.name === name)
        );
      }
      return {
        key,
        name: filterCategoryNames?.get(key) ?? key,
        filters: categoryFilters,
        ...filterRules[key],
      };
    });
    if (isEC && !isSolutions) {
      categories.push(ecFilterRules.partnerLevel);
    }
    if (isSolutions) {
      categories.push({
        ...filterRules.product,
        filters: productsList,
      });
      const solutionFilters = ['geographies', 'solutionType', 'partnerLevel'];
      const missingFilters = solutionFilters
        .filter((filterKey) => !categories.some((category) => category.key === filterKey))
        .map((filterKey) => sppFilterRules[filterKey]);

      categories.push(...missingFilters);
    }

    // Sort categories by sortOrder for SLN view else sort alphabetically by name
    const sortedCategories = categories.sort((a, b) => {
      if (isSolutions) {
        if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
          return a.sortOrder - b.sortOrder;
        }
        if (a.sortOrder !== undefined) return -1;
        if (b.sortOrder !== undefined) return 1;
      }
      return azSort(a.name, b.name);
    });

    if (!isSolutions) {
      sortedCategories.unshift({
        ...filterRules.product,
        filters: productsList,
      });
    }
    return sortedCategories;
  }, [filters, isEC, filterRules, productsList, countries, industries, filterCategoryNames, isSolutions]);

  const accordionSelectedIndexes = useMemo(() => {
    const parsedSearch = qs.parse(location.search);
    return sortedFilterCategories?.map((val, index) => (parsedSearch[val?.key] ? index : null));
  }, [sortedFilterCategories, location.search]);

  const onFiltersChange = (filterType, selectedOptions) => {
    const sort = sortByChangedByUser ? qs.parse(location.search).sort : 'RELEVANCE';
    navigate({
      search: qs.stringify(
        Object.assign({}, qs.parse(location.search), {
          [filterType]: selectedOptions,
          page: 1,
          sort,
        })
      ),
    });
  };

  const listingTypeConfig = {
    cc: {
      name: 'Creative Cloud',
      defaultListingType: 'plugins',
      listingTypes: [
        { key: 'plugins', label: 'Plugins' },
        { key: 'integrations', label: 'Integrations' },
      ],
    },
    ec: {
      name: 'Experience Cloud',
      defaultListingType: 'applications',
      listingTypes: [
        { key: 'applications', label: 'Applications' },
        { key: 'solutions', label: 'Solutions' },
      ],
    },
  };

  return (
    <Flex paddingStart="20px" direction="column" data-testid="browse-sidebar">
      {cloud && (
        <ListingTypePicker
          label={`${listingTypeConfig[cloud].name} listings`}
          options={listingTypeConfig[cloud].listingTypes}
          defaultKey={listingTypeConfig[cloud].defaultListingType}
          cloud={cloud}
        />
      )}
      {shouldShowSearchAndFilters && (
        <>
          <SearchField />
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            {filters && productsList && !countriesLoading && !industriesLoading && !filterCategoryNamesLoading ? (
              <QuietAccordion multiselectable={true} defaultSelectedIndex={accordionSelectedIndexes}>
                {sortedFilterCategories?.map((category) => (
                  <QuietAccordionItem header={category?.name} key={category?.key}>
                    <FilterGroup
                      value={getUrlFilterValue(category?.key, category?.type)}
                      onChange={(selectedOptions) => {
                        onFiltersChange(category?.key, selectedOptions);
                      }}
                      loading={loading}
                      data-testid={`browse-sidebar-checkboxes-${category?.key}`}
                      aria-label={`${category?.key.replace(/([A-Z])/g, ' $1')} filters`}
                      maxFilters={category?.maxFilters}
                      filters={category?.filters}
                      shouldShowCount={category?.shouldShowCount}
                      shouldShowEllipsis={category?.shouldShowEllipsis}
                      shouldUseKeyAsInputValue={category?.shouldUseKeyAsInputValue}
                      type={category?.type}
                      skipSort={category?.skipSort}
                    />
                  </QuietAccordionItem>
                ))}
              </QuietAccordion>
            ) : (
              <WithSkeleton count={20} width="90%" />
            )}
          </ErrorBoundary>
        </>
      )}
    </Flex>
  );
};

export default SideBar;
